import { SectionTitle } from "@/sprout/typography";
import { IconButton } from "@/sprout/icon_button";
import BackIcon from "@/sprout/icons/back";
import ForwardIcon from "@/sprout/icons/forward";
import { useRef, useState, useEffect } from "react";
import { router } from "@inertiajs/react";
import Tooltip from "@sprout/tooltip.tsx";
import QuestionIcon from "@sprout/icons/question.tsx";
import LoadingSpinner from "@sprout/loading_spinner.tsx";
import CompanyLogo from "@components/company_logo.tsx";
import JobRecommendationCard, { type JobRecommendation } from "./job_recommendation_card.tsx";
import { type RecommendationFeedback } from "./recommendation_feedback_buttons.tsx";
import { useShowFlash } from "@sprout/flash.tsx";
import useTranslation from "@/hooks/use_translation";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ApplicationForm } from "@components/job_post/application_form";
import { ApplicationSuccess } from "@components/job_post/application_success";
import DiscardProgressModal from "@components/job_post/discard_progress_modal";
import type { JobPost, SearchSpecificFields } from "@/types/jben/job_post";
import type { BoardConfiguration } from "@/types/jben/board_configuration";
import { useSafePostHog } from "@hooks/use_posthog";

const MINI_CARD_WIDTH = 220;

type ApplyModalData = {
  jobPost: JobPost & SearchSpecificFields;
  boardConfiguration: BoardConfiguration;
  submitPath: string;
  educationApiUrl?: string;
  dreamJobAvailable?: boolean;
};

const JobRecommendationsLoading = () => {
  const { t } = useTranslation("dashboard");

  return (
    <div className="min-w-0 rounded-lg bg-white p-6 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <SectionTitle>{t("job_recommendations.title")}</SectionTitle>
        <Tooltip text={t("job_recommendations.tooltip")}>
          <QuestionIcon size="sm" />
        </Tooltip>
      </div>
      <div className="flex justify-center py-8">
        <LoadingSpinner size="medium" />
      </div>
    </div>
  );
};

const JobRecommendationsContent = ({
  jobRecommendations,
}: {
  jobRecommendations: JobRecommendation[];
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [applyModalData, setApplyModalData] = useState<ApplyModalData | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isLoadingJob, setIsLoadingJob] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [feedbackOverrides, setFeedbackOverrides] = useState<
    Record<number, RecommendationFeedback | null>
  >({});
  const dirtyRef = useRef<() => boolean>(() => false);

  const showFlash = useShowFlash();
  const { t } = useTranslation("dashboard");
  const posthog = useSafePostHog();

  useEffect(() => {
    const updateScrollState = () => {
      if (!scrollerRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollerRef.current;
      setCanScroll(scrollWidth > clientWidth + 1);
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft >= scrollWidth - clientWidth - 1);
    };

    const handleResize = () => {
      requestAnimationFrame(updateScrollState);
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    const scroller = scrollerRef.current;
    if (scroller) scroller.addEventListener("scroll", updateScrollState);

    return () => {
      resizeObserver.disconnect();
      scroller?.removeEventListener("scroll", updateScrollState);
    };
  }, [jobRecommendations]);

  const selected = jobRecommendations[selectedIndex];
  const currentFeedback =
    selected.id in feedbackOverrides ? feedbackOverrides[selected.id] : selected.feedback;

  const handleFeedback = (next: RecommendationFeedback | null) => {
    const previous = currentFeedback;

    setFeedbackOverrides((overrides) => ({ ...overrides, [selected.id]: next }));

    router.post(
      selected.feedbackPath,
      { feedback: next ?? "" },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          posthog?.capture("recommendation_feedback_submitted", {
            job_post_id: selected.id,
            feedback: next,
            previous_feedback: previous,
            source: "dashboard",
          });
        },
        onError: () => {
          setFeedbackOverrides((overrides) => ({ ...overrides, [selected.id]: previous }));
          showFlash(t("errors.there_was_a_problem"), "error");
        },
      },
    );
  };

  const prev = () => {
    scrollerRef.current?.scrollBy({ left: -MINI_CARD_WIDTH, behavior: "smooth" });
  };

  const next = () => {
    scrollerRef.current?.scrollBy({ left: MINI_CARD_WIDTH, behavior: "smooth" });
  };

  const handleViewJob = async (job: JobRecommendation) => {
    posthog?.capture("card_clicked", {
      card_type: "recommendation",
      source: "dashboard",
      job_post_id: job.id,
      job_title: job.title,
      company_name: job.companyName,
      previously_viewed: job.viewed,
    });
    if (!job.viewed) {
      router.replaceProp(
        "jobRecommendations",
        jobRecommendations.map((rec) => (rec.id === job.id ? { ...rec, viewed: true } : rec)),
      );
    }
    setIsLoadingJob(true);
    try {
      const response = await fetch(job.viewJobPath, {
        headers: { "X-Modal-Request": "true", Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const data = await response.json();
      setApplyModalData(data);
      setSubmitted(false);
    } catch {
      showFlash(t("errors.there_was_a_problem"), "error");
    } finally {
      setIsLoadingJob(false);
    }
  };

  const closeModal = () => {
    setApplyModalData(null);
    setSubmitted(false);
    setShowDiscardConfirm(false);
  };

  return (
    <div className="min-w-0 rounded-lg bg-white p-6 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <SectionTitle>{t("job_recommendations.title")}</SectionTitle>
        <Tooltip text={t("job_recommendations.tooltip")}>
          <QuestionIcon size="sm" />
        </Tooltip>
      </div>

      <JobRecommendationCard
        job={selected}
        onViewJob={() => handleViewJob(selected)}
        isLoading={isLoadingJob}
        feedback={currentFeedback}
        onFeedback={handleFeedback}
      />

      <div ref={containerRef}>
        <div className="flex gap-3 overflow-x-hidden" ref={scrollerRef}>
          {jobRecommendations.map((job, index) => {
            const isSelected = index === selectedIndex;
            return (
              <button
                key={job.id}
                onClick={() => setSelectedIndex(index)}
                style={{ padding: "16px 20px", width: MINI_CARD_WIDTH }}
                className={[
                  "flex flex-none gap-3 items-center rounded-lg border text-left min-w-0",
                  isSelected
                    ? "bg-[var(--extended-green-e25,#ecfaf5)] border-2 border-[#047957]"
                    : "bg-white border-[var(--object-strokes-dividers,#ccd4d1)]",
                ].join(" ")}
              >
                <CompanyLogo companyName={job.companyName} logoUrl={job.logoUrl} size="small" />
                <div className="flex flex-col min-w-0">
                  <p className="font-medium text-sm leading-5 text-[var(--text-primary,#15372c)] truncate">
                    {job.title}
                  </p>
                  <p className="text-sm leading-5 text-[var(--text-primary,#15372c)] truncate">
                    {job.companyName}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {canScroll && (
          <div className="flex justify-end mt-2">
            <IconButton
              label={t("job_recommendations.scroll_left")}
              icon={BackIcon}
              size="md"
              onClick={prev}
              disabled={isAtStart}
            />
            <IconButton
              label={t("job_recommendations.scroll_right")}
              icon={ForwardIcon}
              size="md"
              onClick={next}
              disabled={isAtEnd}
            />
          </div>
        )}
      </div>

      {applyModalData && <div className="fixed inset-0 z-40 bg-gray-900/30" aria-hidden="true" />}
      <Dialog
        open={!!applyModalData}
        modal={false}
        onOpenChange={(open) => {
          if (open) return;
          if (dirtyRef.current() && !submitted) {
            setShowDiscardConfirm(true);
          } else {
            closeModal();
          }
        }}
      >
        <DialogContent
          className="w-[90vw] sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-8 gap-0 [&>button]:rounded-full [&>button]:bg-white [&>button]:p-2 [&>button]:opacity-90 [&>button]:shadow-md [&>button]:data-[state=open]:bg-white"
          hideClose={false}
          onInteractOutside={(event) => event.preventDefault()}
        >
          {applyModalData &&
            (submitted ? (
              <ApplicationSuccess jobPost={applyModalData.jobPost} />
            ) : (
              <ApplicationForm
                jobPost={applyModalData.jobPost}
                questions={applyModalData.jobPost.questions}
                submitPath={applyModalData.submitPath}
                boardConfiguration={applyModalData.boardConfiguration}
                onSuccess={() => setSubmitted(true)}
                educationApiUrl={applyModalData.educationApiUrl}
                dreamJobAvailable={applyModalData.dreamJobAvailable}
                isRecommendation
                dirtyRef={dirtyRef}
              />
            ))}
        </DialogContent>
      </Dialog>

      <DiscardProgressModal
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        onDiscard={closeModal}
      />
    </div>
  );
};

const JobRecommendationsSection = () => {
  const [jobRecommendations, setJobRecommendations] = useState<JobRecommendation[] | null>(null);

  useEffect(() => {
    let active = true;

    fetch("/recommendations", { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : []))
      .then((data: JobRecommendation[]) => {
        if (active) setJobRecommendations(data);
      })
      .catch(() => {
        if (active) setJobRecommendations([]);
      });

    return () => {
      active = false;
    };
  }, []);

  if (jobRecommendations === null) return <JobRecommendationsLoading />;
  if (jobRecommendations.length === 0) return null;

  return <JobRecommendationsContent jobRecommendations={jobRecommendations} />;
};

export default JobRecommendationsSection;
